import { scenarioOperationTypeSchema } from '@tempo-io/adaptive-planner-dto';
import { createPageQueryDTOSchema } from '@tempo-io/common-dto';
import { z } from 'zod';

export const NOTIFICATION_TYPES = ['organization', 'subscription'] as const;
export type NotificationTypes = (typeof NOTIFICATION_TYPES)[number];

export const RECOMMENDATION_MOVE_CATEGORIES = [
  'recommendation_assigned',
  'recommendation_completed',
  'recommendation_dismissed',
  'recommendation_reopened',
  'recommendation_restored',
  'recommendation_unassigned',
  'recommendation_review',
  'recommendation_autoclosed',
  'recommendation_stale',
  'recommendation_kept',
] as const;

export const ORGANIZATION_CATEGORIES = [
  'invites',
  'work_integration_reauth',
  'work_integration_schedule_disabled',
  'work_integration_sync_error',
  'work_integration_setup_complete',
  'work_integration_disconnected',
  'work_integration_excluded_teams',
  'work_integration_publish_complete',
  'capacity_orphaned_instance',
  'portfolio_created',
  'team_assignment_changed',
  'plan_published',
  'scenario_operation_failed',
  'demand_framework_changed',
  'demand_reviewer_assigned',
  'demand_stage_changed',
  'objective_health_changed',
  'objective_drift_detected',
  'new_critical_insight',
  'new_high_insight',
  'credit_pool_low',
  ...RECOMMENDATION_MOVE_CATEGORIES,
] as const;
export type OrganizationCategories = (typeof ORGANIZATION_CATEGORIES)[number];

export const SUBSCRIPTION_CATEGORIES = [
  'feedback',
  'idea_comments',
  'comment_mention',
  'stale_ideas',
  'export_completion',
  'ai_dashboard_generation_completed',
] as const;
export type SubscriptionCategories = (typeof SUBSCRIPTION_CATEGORIES)[number];

export const NOTIFICATION_CATEGORIES = SUBSCRIPTION_CATEGORIES;
export type NotificationCategories = SubscriptionCategories;

export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export const organizationCategorySchema = z.enum(ORGANIZATION_CATEGORIES);
export const subscriptionCategorySchema = z.enum(SUBSCRIPTION_CATEGORIES);

export const NOTIFICATION_CHANNELS = ['email', 'in_app', 'slack'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];
export const notificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);

export const invitesDataSchema = z.object({
  inviteeUserId: z.string(),
  inviteeName: z.string().optional(),
  organizationDisplayName: z.string().optional(),
});

// Read-only: nothing dispatches trials/trial_extended any more, but stored rows still render
// in the inbox until their expiresAt (the trial end date) passes.
export const trialsDataSchema = z.object({
  organizationDisplayName: z.string(),
  daysRemaining: z.number().optional(),
});

export const trialExtendedDataSchema = z.object({
  organizationDisplayName: z.string(),
  endDate: z.string(),
});

export const WORK_INTEGRATION_REAUTH_STAGES = [
  'initial',
  'reminder',
  'final',
  'anonymized',
] as const;
export const workIntegrationReauthStageSchema = z.enum(WORK_INTEGRATION_REAUTH_STAGES);

export const workIntegrationReauthDataSchema = z.object({
  stage: workIntegrationReauthStageSchema.optional(),
  sourceSystemType: z.string().optional(),
});

export const WORK_INTEGRATION_SCHEDULE_DISABLED_REASONS = [
  'repeated_failures',
  'inactive_organization',
] as const;
export const workIntegrationScheduleDisabledReasonSchema = z.enum(
  WORK_INTEGRATION_SCHEDULE_DISABLED_REASONS,
);

export const workIntegrationScheduleDisabledDataSchema = z.object({
  sourceSystemType: z.string().optional(),
  integrationName: z.string().optional(),
  reasonCode: workIntegrationScheduleDisabledReasonSchema.optional(),
});

export const workIntegrationSyncErrorDataSchema = z.object({
  sourceSystemType: z.string().optional(),
  integrationName: z.string().optional(),
  lastSuccessfulSyncAt: z.string().nullable().optional(),
});

export const workIntegrationSetupCompleteDataSchema = z.object({
  sourceSystemType: z.string().optional(),
  integrationName: z.string().optional(),
});

export const workIntegrationDisconnectedDataSchema = z.object({
  sourceSystemType: z.string().optional(),
  integrationName: z.string().optional(),
  portfolioName: z.string().optional(),
});

export const excludedTeamSummarySchema = z.object({
  teamName: z.string(),
  loopName: z.string(),
});

export const workIntegrationExcludedTeamsDataSchema = z.object({
  sourceSystemType: z.string().optional(),
  integrationName: z.string().optional(),
  excludedTeams: z.array(excludedTeamSummarySchema),
});

export const workIntegrationPublishCompleteDataSchema = z.object({
  systemName: z.string().optional(),
  portfolioName: z.string().optional(),
});

export const capacityOrphanedInstanceDataSchema = z.object({
  instanceId: z.string(),
  instanceName: z.string().nullish(),
});

export const demandFrameworkChangedDataSchema = z.object({
  previousFrameworkName: z.string().min(1),
  newFrameworkName: z.string().min(1),
});

export const demandReviewerAssignedDataSchema = z.object({
  demandId: z.number().int(),
  demandTitle: z.string(),
  loopId: z.number().int().optional(),
  actionUrl: z.string().url(),
  /**
   * The requester's message, as a preview with mention markup already flattened. FIM does not store
   * it, so this payload is the only place it exists outside the reviewer's inbox.
   */
  note: z.string().optional(),
});

export const demandStageChangedDataSchema = z.object({
  demandId: z.number().int(),
  demandTitle: z.string(),
  stageName: z.string(),
  loopId: z.number().int().optional(),
  actionUrl: z.string().url(),
});

export const emptyDataSchema = z.object({});

export type InvitesData = z.infer<typeof invitesDataSchema>;
export type TrialsData = z.infer<typeof trialsDataSchema>;
export type WorkIntegrationReauthStage = z.infer<typeof workIntegrationReauthStageSchema>;
export type WorkIntegrationReauthData = z.infer<typeof workIntegrationReauthDataSchema>;
export type WorkIntegrationScheduleDisabledReason = z.infer<
  typeof workIntegrationScheduleDisabledReasonSchema
>;
export type WorkIntegrationScheduleDisabledData = z.infer<
  typeof workIntegrationScheduleDisabledDataSchema
>;
export type WorkIntegrationSyncErrorData = z.infer<typeof workIntegrationSyncErrorDataSchema>;
export type WorkIntegrationSetupCompleteData = z.infer<
  typeof workIntegrationSetupCompleteDataSchema
>;
export type WorkIntegrationDisconnectedData = z.infer<typeof workIntegrationDisconnectedDataSchema>;
export type ExcludedTeamSummary = z.infer<typeof excludedTeamSummarySchema>;
export type WorkIntegrationExcludedTeamsData = z.infer<
  typeof workIntegrationExcludedTeamsDataSchema
>;
export type WorkIntegrationPublishCompleteData = z.infer<
  typeof workIntegrationPublishCompleteDataSchema
>;
export type CapacityOrphanedInstanceData = z.infer<typeof capacityOrphanedInstanceDataSchema>;
export type DemandFrameworkChangedData = z.infer<typeof demandFrameworkChangedDataSchema>;
export type DemandReviewerAssignedData = z.infer<typeof demandReviewerAssignedDataSchema>;
export type DemandStageChangedData = z.infer<typeof demandStageChangedDataSchema>;

export const invitesVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('invites'),
  data: invitesDataSchema,
});

export const trialsVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('trials'),
  data: trialsDataSchema,
});

export const trialExtendedVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('trial_extended'),
  data: trialExtendedDataSchema,
});

export const workIntegrationReauthVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('work_integration_reauth'),
  data: workIntegrationReauthDataSchema,
});

export const workIntegrationScheduleDisabledVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('work_integration_schedule_disabled'),
  data: workIntegrationScheduleDisabledDataSchema,
});

export const workIntegrationSyncErrorVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('work_integration_sync_error'),
  data: workIntegrationSyncErrorDataSchema,
});

export const workIntegrationSetupCompleteVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('work_integration_setup_complete'),
  data: workIntegrationSetupCompleteDataSchema,
});

export const workIntegrationDisconnectedVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('work_integration_disconnected'),
  data: workIntegrationDisconnectedDataSchema,
});

export const workIntegrationExcludedTeamsVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('work_integration_excluded_teams'),
  data: workIntegrationExcludedTeamsDataSchema,
});

export const workIntegrationPublishCompleteVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('work_integration_publish_complete'),
  data: workIntegrationPublishCompleteDataSchema,
});

export const capacityOrphanedInstanceVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('capacity_orphaned_instance'),
  data: capacityOrphanedInstanceDataSchema,
});

export const portfolioCreatedDataSchema = z.object({
  loopId: z.number().int(),
  creatorUserId: z.string(),
});

export type PortfolioCreatedData = z.infer<typeof portfolioCreatedDataSchema>;

export const portfolioCreatedVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('portfolio_created'),
  data: portfolioCreatedDataSchema,
});

export const demandFrameworkChangedVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('demand_framework_changed'),
  data: demandFrameworkChangedDataSchema,
});

export const demandReviewerAssignedVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('demand_reviewer_assigned'),
  data: demandReviewerAssignedDataSchema,
});

export const demandStageChangedVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('demand_stage_changed'),
  data: demandStageChangedDataSchema,
});

export const teamAssignmentChangedDataSchema = z.object({
  portfolioName: z.string(),
  changedItemCount: z.number().int(),
  loopId: z.number().int(),
});

export type TeamAssignmentChangedData = z.infer<typeof teamAssignmentChangedDataSchema>;

export const teamAssignmentChangedVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('team_assignment_changed'),
  data: teamAssignmentChangedDataSchema,
});

export const planPublishedDataSchema = z.object({
  portfolioName: z.string(),
  publishedByUserId: z.string(),
  loopId: z.number().int(),
});

export type PlanPublishedData = z.infer<typeof planPublishedDataSchema>;

export const planPublishedVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('plan_published'),
  data: planPublishedDataSchema,
});

// FAILED is a positively determined failure (a deleted scenario, a rejected payload); EXPIRED is a
// stranded or retry-exhausted operation the liveness sweep resolved.
export const scenarioOperationFailedDataSchema = z.object({
  operationId: z.number().int(),
  loopId: z.number().int(),
  scenarioId: z.number().int(),
  operationType: scenarioOperationTypeSchema,
  status: z.enum(['FAILED', 'EXPIRED']),
});

export type ScenarioOperationFailedData = z.infer<typeof scenarioOperationFailedDataSchema>;

export const scenarioOperationFailedVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('scenario_operation_failed'),
  data: scenarioOperationFailedDataSchema,
});

export const objectiveHealthStatusSchema = z.enum([
  'HEALTHY',
  'NEEDS_ATTENTION',
  'ACTION_REQUIRED',
  'DONE',
]);

export const objectiveHealthChangedDataSchema = z.object({
  objectiveId: z.number().int(),
  objectiveName: z.string(),
  newStatus: objectiveHealthStatusSchema,
  portfolioName: z.string().nullish(),
  loopId: z.number().int().nullish(),
  portfolioCount: z.number().int(),
});

export type ObjectiveHealthChangedData = z.infer<typeof objectiveHealthChangedDataSchema>;

export const objectiveHealthChangedVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('objective_health_changed'),
  data: objectiveHealthChangedDataSchema,
});

export const objectiveDriftDetectedDataSchema = z.object({
  objectiveId: z.number().int(),
  objectiveName: z.string(),
  previousStatus: objectiveHealthStatusSchema,
  newStatus: objectiveHealthStatusSchema,
  portfolioName: z.string().nullish(),
  loopId: z.number().int().nullish(),
  portfolioCount: z.number().int(),
});

export type ObjectiveDriftDetectedData = z.infer<typeof objectiveDriftDetectedDataSchema>;

export const objectiveDriftDetectedVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('objective_drift_detected'),
  data: objectiveDriftDetectedDataSchema,
});

export const newInsightDataSchema = z.object({
  insightId: z.string(),
  insightTitle: z.string(),
  portfolioName: z.string().nullish(),
  loopId: z.number().int().nullish(),
});
export type NewInsightData = z.infer<typeof newInsightDataSchema>;

export const newCriticalInsightVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('new_critical_insight'),
  data: newInsightDataSchema,
});

export const newHighInsightVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('new_high_insight'),
  data: newInsightDataSchema,
});

export const creditPoolLowDataSchema = z.object({
  organizationDisplayName: z.string(),
});
export type CreditPoolLowData = z.infer<typeof creditPoolLowDataSchema>;

export const creditPoolLowVariablesSchema = z.object({
  type: z.literal('organization'),
  category: z.literal('credit_pool_low'),
  data: creditPoolLowDataSchema,
});

export const recommendationMoveDataSchema = z.object({
  trackedItemId: z.string(),
  findingId: z.string(),
  recommendationId: z.string(),
  recommendationTitle: z.string(),
  /** Null when the item was created by the move being notified. */
  fromStatus: z.string().nullable(),
  toStatus: z.string(),
  actorUserId: z.string().nullable(),
  changeOrigin: z.enum(['user', 'system']),
  note: z.string().optional(),
  reason: z.string().optional(),
  closeReason: z.enum(['signal_cleared', 'data_unavailable']).optional(),
  loopId: z.number().int().optional(),
  actionUrl: z.string().url(),
});
export type RecommendationMoveData = z.infer<typeof recommendationMoveDataSchema>;

const makeRecommendationMoveVariablesSchema = <
  C extends (typeof RECOMMENDATION_MOVE_CATEGORIES)[number],
>(
  category: C,
) =>
  z.object({
    type: z.literal('organization'),
    category: z.literal(category),
    data: recommendationMoveDataSchema,
  });

export const recommendationAssignedVariablesSchema =
  makeRecommendationMoveVariablesSchema('recommendation_assigned');
export const recommendationCompletedVariablesSchema = makeRecommendationMoveVariablesSchema(
  'recommendation_completed',
);
export const recommendationDismissedVariablesSchema = makeRecommendationMoveVariablesSchema(
  'recommendation_dismissed',
);
export const recommendationReopenedVariablesSchema =
  makeRecommendationMoveVariablesSchema('recommendation_reopened');
export const recommendationRestoredVariablesSchema =
  makeRecommendationMoveVariablesSchema('recommendation_restored');
export const recommendationUnassignedVariablesSchema = makeRecommendationMoveVariablesSchema(
  'recommendation_unassigned',
);
export const recommendationReviewVariablesSchema =
  makeRecommendationMoveVariablesSchema('recommendation_review');
export const recommendationAutoclosedVariablesSchema = makeRecommendationMoveVariablesSchema(
  'recommendation_autoclosed',
);
export const recommendationStaleVariablesSchema =
  makeRecommendationMoveVariablesSchema('recommendation_stale');
export const recommendationKeptVariablesSchema =
  makeRecommendationMoveVariablesSchema('recommendation_kept');

export const feedbackDataSchema = z.object({
  feedbackSummary: z.string(),
  feedbackDescription: z.string().optional(),
  feedbackFormName: z.string(),
  actionUrl: z.string().url(),
  submitterName: z.string().optional(),
  fields: z
    .array(
      z.object({
        name: z.string(),
        values: z.array(z.string()),
      }),
    )
    .optional(),
  attachments: z.array(z.string()).optional(),
});

export const ideaCommentsDataSchema = z.object({
  commentText: z.string(),
  ideaTitle: z.string(),
  actionUrl: z.string().url(),
  commenterName: z.string().optional(),
});

export const COMMENT_MENTION_ENTITY_TYPES = ['idea', 'demand'] as const;
export const commentMentionEntityTypeSchema = z.enum(COMMENT_MENTION_ENTITY_TYPES);
export type CommentMentionEntityType = z.infer<typeof commentMentionEntityTypeSchema>;

export const commentMentionDataSchema = z.object({
  entityType: commentMentionEntityTypeSchema,
  entityId: z.number(),
  entityTitle: z.string(),
  commentId: z.number(),
  commentText: z.string(),
  actionUrl: z.string().url(),
  commenterName: z.string().optional(),
});

export const staleIdeasDataSchema = z.object({
  staleIdeasCount: z.number(),
  ideaSamples: z.array(
    z.object({
      id: z.number(),
      title: z.string(),
    }),
  ),
  actionUrl: z.string().url(),
});

// `target` is optional, and absent means 'visualization': a notification published before
// this field existed can still be in the dispatch queue during a rolling deploy.
const exportCompletionBase = {
  taskId: z.number().int(),
  loopId: z.number().int(),
  format: z.enum(['png', 'pdf', 'svg']),
  success: z.boolean(),
};

export const exportCompletionDataSchema = z.union([
  z.object({
    ...exportCompletionBase,
    target: z.literal('dashboard'),
    dashboardId: z.number().int(),
  }),
  z.object({
    ...exportCompletionBase,
    target: z.literal('visualization').optional(),
    viewId: z.number().int(),
    visualizationId: z.number().int(),
    visualizationType: z.enum(['swimlane', 'timeline', 'table']),
  }),
]);

export const aiDashboardGenerationCompletedDataSchema = z.object({
  taskId: z.number().int(),
  dashboardId: z.number().int().nullable(),
  success: z.boolean(),
});

export type FeedbackData = z.infer<typeof feedbackDataSchema>;
export type IdeaCommentsData = z.infer<typeof ideaCommentsDataSchema>;
export type CommentMentionData = z.infer<typeof commentMentionDataSchema>;
export type StaleIdeasData = z.infer<typeof staleIdeasDataSchema>;
export type ExportCompletionData = z.infer<typeof exportCompletionDataSchema>;
export type AiDashboardGenerationCompletedData = z.infer<
  typeof aiDashboardGenerationCompletedDataSchema
>;

export const feedbackVariablesSchema = z.object({
  type: z.literal('subscription'),
  category: z.literal('feedback'),
  data: feedbackDataSchema,
});

export const ideaCommentsVariablesSchema = z.object({
  type: z.literal('subscription'),
  category: z.literal('idea_comments'),
  data: ideaCommentsDataSchema,
});

export const commentMentionVariablesSchema = z.object({
  type: z.literal('subscription'),
  category: z.literal('comment_mention'),
  data: commentMentionDataSchema,
});

export const staleIdeasVariablesSchema = z.object({
  type: z.literal('subscription'),
  category: z.literal('stale_ideas'),
  data: staleIdeasDataSchema,
});

export const exportCompletionVariablesSchema = z.object({
  type: z.literal('subscription'),
  category: z.literal('export_completion'),
  data: exportCompletionDataSchema,
});

export const aiDashboardGenerationCompletedVariablesSchema = z.object({
  type: z.literal('subscription'),
  category: z.literal('ai_dashboard_generation_completed'),
  data: aiDashboardGenerationCompletedDataSchema,
});

export const notificationVariablesSchema = z.discriminatedUnion('type', [
  z.discriminatedUnion('category', [
    invitesVariablesSchema,
    trialsVariablesSchema,
    trialExtendedVariablesSchema,
    workIntegrationReauthVariablesSchema,
    workIntegrationScheduleDisabledVariablesSchema,
    workIntegrationSyncErrorVariablesSchema,
    workIntegrationSetupCompleteVariablesSchema,
    workIntegrationDisconnectedVariablesSchema,
    workIntegrationExcludedTeamsVariablesSchema,
    workIntegrationPublishCompleteVariablesSchema,
    capacityOrphanedInstanceVariablesSchema,
    portfolioCreatedVariablesSchema,
    teamAssignmentChangedVariablesSchema,
    planPublishedVariablesSchema,
    scenarioOperationFailedVariablesSchema,
    demandFrameworkChangedVariablesSchema,
    demandReviewerAssignedVariablesSchema,
    demandStageChangedVariablesSchema,
    objectiveHealthChangedVariablesSchema,
    objectiveDriftDetectedVariablesSchema,
    newCriticalInsightVariablesSchema,
    newHighInsightVariablesSchema,
    creditPoolLowVariablesSchema,
    recommendationAssignedVariablesSchema,
    recommendationCompletedVariablesSchema,
    recommendationDismissedVariablesSchema,
    recommendationReopenedVariablesSchema,
    recommendationRestoredVariablesSchema,
    recommendationUnassignedVariablesSchema,
    recommendationReviewVariablesSchema,
    recommendationAutoclosedVariablesSchema,
    recommendationStaleVariablesSchema,
    recommendationKeptVariablesSchema,
  ]),
  z.discriminatedUnion('category', [
    feedbackVariablesSchema,
    ideaCommentsVariablesSchema,
    commentMentionVariablesSchema,
    exportCompletionVariablesSchema,
    staleIdeasVariablesSchema,
    aiDashboardGenerationCompletedVariablesSchema,
  ]),
]);

export type InvitesVariables = z.infer<typeof invitesVariablesSchema>;
export type TrialsVariables = z.infer<typeof trialsVariablesSchema>;
export type TrialExtendedVariables = z.infer<typeof trialExtendedVariablesSchema>;
export type WorkIntegrationReauthVariables = z.infer<typeof workIntegrationReauthVariablesSchema>;
export type WorkIntegrationScheduleDisabledVariables = z.infer<
  typeof workIntegrationScheduleDisabledVariablesSchema
>;
export type WorkIntegrationSyncErrorVariables = z.infer<
  typeof workIntegrationSyncErrorVariablesSchema
>;
export type WorkIntegrationSetupCompleteVariables = z.infer<
  typeof workIntegrationSetupCompleteVariablesSchema
>;
export type WorkIntegrationDisconnectedVariables = z.infer<
  typeof workIntegrationDisconnectedVariablesSchema
>;
export type WorkIntegrationExcludedTeamsVariables = z.infer<
  typeof workIntegrationExcludedTeamsVariablesSchema
>;
export type WorkIntegrationPublishCompleteVariables = z.infer<
  typeof workIntegrationPublishCompleteVariablesSchema
>;
export type CapacityOrphanedInstanceVariables = z.infer<
  typeof capacityOrphanedInstanceVariablesSchema
>;
export type PortfolioCreatedVariables = z.infer<typeof portfolioCreatedVariablesSchema>;
export type TeamAssignmentChangedVariables = z.infer<typeof teamAssignmentChangedVariablesSchema>;
export type PlanPublishedVariables = z.infer<typeof planPublishedVariablesSchema>;
export type ScenarioOperationFailedVariables = z.infer<
  typeof scenarioOperationFailedVariablesSchema
>;
export type DemandFrameworkChangedVariables = z.infer<typeof demandFrameworkChangedVariablesSchema>;
export type DemandReviewerAssignedVariables = z.infer<typeof demandReviewerAssignedVariablesSchema>;
export type DemandStageChangedVariables = z.infer<typeof demandStageChangedVariablesSchema>;
export type ObjectiveHealthChangedVariables = z.infer<typeof objectiveHealthChangedVariablesSchema>;
export type ObjectiveDriftDetectedVariables = z.infer<typeof objectiveDriftDetectedVariablesSchema>;
export type FeedbackVariables = z.infer<typeof feedbackVariablesSchema>;
export type IdeaCommentsVariables = z.infer<typeof ideaCommentsVariablesSchema>;
export type StaleIdeasVariables = z.infer<typeof staleIdeasVariablesSchema>;
export type ExportCompletionVariables = z.infer<typeof exportCompletionVariablesSchema>;
export type RecommendationAssignedVariables = z.infer<typeof recommendationAssignedVariablesSchema>;
export type RecommendationCompletedVariables = z.infer<
  typeof recommendationCompletedVariablesSchema
>;
export type RecommendationDismissedVariables = z.infer<
  typeof recommendationDismissedVariablesSchema
>;
export type RecommendationReopenedVariables = z.infer<typeof recommendationReopenedVariablesSchema>;
export type RecommendationUnassignedVariables = z.infer<
  typeof recommendationUnassignedVariablesSchema
>;
export type RecommendationRestoredVariables = z.infer<typeof recommendationRestoredVariablesSchema>;
export type RecommendationReviewVariables = z.infer<typeof recommendationReviewVariablesSchema>;
export type RecommendationAutoclosedVariables = z.infer<
  typeof recommendationAutoclosedVariablesSchema
>;
export type RecommendationStaleVariables = z.infer<typeof recommendationStaleVariablesSchema>;
export type RecommendationKeptVariables = z.infer<typeof recommendationKeptVariablesSchema>;
export type NotificationVariables = z.infer<typeof notificationVariablesSchema>;

export type NotificationCategory = NotificationVariables['category'];

// Do not delete until pre-rename DB rows (notifications + preferences) have aged out —
// removing this map early will silently drop legacy-category rows on read.
export const LEGACY_CATEGORY_ALIASES: Record<string, NotificationCategory> = {
  jira_reauth: 'work_integration_reauth',
};

export const normalizeNotificationCategory = (category: string): string =>
  LEGACY_CATEGORY_ALIASES[category] ?? category;

export const buildCommentMentionActionUrl = (params: {
  appBaseUrl: string;
  organizationId: string;
  entityType: CommentMentionEntityType;
  entityId: number;
  commentId: number;
}): string => {
  const { appBaseUrl, organizationId, entityType, entityId, commentId } = params;
  const base = `${appBaseUrl}/org-id/${organizationId}/intake`;
  switch (entityType) {
    case 'demand':
      return `${base}/demands?demandId=${entityId}&commentId=${commentId}`;
    default:
      return `${base}/ideas/all?ideaId=${entityId}&commentId=${commentId}`;
  }
};

const notificationBaseSchema = z.object({
  id: z.number(),
  organizationId: z.string(),
  userId: z.string().nullable(),
  type: notificationTypeSchema,
  readAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const invitesNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: invitesVariablesSchema,
});

const trialsNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: trialsVariablesSchema,
});

const trialExtendedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: trialExtendedVariablesSchema,
});

const workIntegrationReauthNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: workIntegrationReauthVariablesSchema,
});

const workIntegrationScheduleDisabledNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: workIntegrationScheduleDisabledVariablesSchema,
});

const workIntegrationSyncErrorNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: workIntegrationSyncErrorVariablesSchema,
});

const workIntegrationSetupCompleteNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: workIntegrationSetupCompleteVariablesSchema,
});

const workIntegrationDisconnectedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: workIntegrationDisconnectedVariablesSchema,
});

const workIntegrationExcludedTeamsNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: workIntegrationExcludedTeamsVariablesSchema,
});

const workIntegrationPublishCompleteNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: workIntegrationPublishCompleteVariablesSchema,
});

const capacityOrphanedInstanceNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: capacityOrphanedInstanceVariablesSchema,
});

const portfolioCreatedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: portfolioCreatedVariablesSchema,
});

const teamAssignmentChangedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: teamAssignmentChangedVariablesSchema,
});

const planPublishedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: planPublishedVariablesSchema,
});

const scenarioOperationFailedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: scenarioOperationFailedVariablesSchema,
});

const demandReviewerAssignedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: demandReviewerAssignedVariablesSchema,
});

const demandStageChangedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: demandStageChangedVariablesSchema,
});

const objectiveHealthChangedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: objectiveHealthChangedVariablesSchema,
});

const objectiveDriftDetectedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: objectiveDriftDetectedVariablesSchema,
});

const newCriticalInsightNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: newCriticalInsightVariablesSchema,
});

const newHighInsightNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: newHighInsightVariablesSchema,
});

const creditPoolLowNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: creditPoolLowVariablesSchema,
});

const recommendationAssignedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: recommendationAssignedVariablesSchema,
});

const recommendationCompletedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: recommendationCompletedVariablesSchema,
});

const recommendationDismissedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: recommendationDismissedVariablesSchema,
});

const recommendationReopenedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: recommendationReopenedVariablesSchema,
});

const recommendationRestoredNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: recommendationRestoredVariablesSchema,
});

const recommendationUnassignedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: recommendationUnassignedVariablesSchema,
});

const recommendationReviewNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: recommendationReviewVariablesSchema,
});

const recommendationAutoclosedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: recommendationAutoclosedVariablesSchema,
});

const recommendationStaleNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: recommendationStaleVariablesSchema,
});

const recommendationKeptNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('organization'),
  variables: recommendationKeptVariablesSchema,
});

const feedbackNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('subscription'),
  variables: feedbackVariablesSchema,
});

const ideaCommentsNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('subscription'),
  variables: ideaCommentsVariablesSchema,
});

const exportCompletionNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('subscription'),
  variables: exportCompletionVariablesSchema,
});

const aiDashboardGenerationCompletedNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('subscription'),
  variables: aiDashboardGenerationCompletedVariablesSchema,
});

const staleIdeasNotificationSchema = notificationBaseSchema.extend({
  type: z.literal('subscription'),
  variables: staleIdeasVariablesSchema,
});

export const notificationDTOSchema = z.union([
  invitesNotificationSchema,
  trialsNotificationSchema,
  trialExtendedNotificationSchema,
  workIntegrationReauthNotificationSchema,
  workIntegrationScheduleDisabledNotificationSchema,
  workIntegrationSyncErrorNotificationSchema,
  workIntegrationSetupCompleteNotificationSchema,
  workIntegrationDisconnectedNotificationSchema,
  workIntegrationExcludedTeamsNotificationSchema,
  workIntegrationPublishCompleteNotificationSchema,
  capacityOrphanedInstanceNotificationSchema,
  portfolioCreatedNotificationSchema,
  teamAssignmentChangedNotificationSchema,
  planPublishedNotificationSchema,
  scenarioOperationFailedNotificationSchema,
  demandReviewerAssignedNotificationSchema,
  demandStageChangedNotificationSchema,
  objectiveHealthChangedNotificationSchema,
  objectiveDriftDetectedNotificationSchema,
  newCriticalInsightNotificationSchema,
  newHighInsightNotificationSchema,
  creditPoolLowNotificationSchema,
  recommendationAssignedNotificationSchema,
  recommendationCompletedNotificationSchema,
  recommendationDismissedNotificationSchema,
  recommendationReopenedNotificationSchema,
  recommendationRestoredNotificationSchema,
  recommendationUnassignedNotificationSchema,
  recommendationReviewNotificationSchema,
  recommendationAutoclosedNotificationSchema,
  recommendationStaleNotificationSchema,
  recommendationKeptNotificationSchema,
  feedbackNotificationSchema,
  ideaCommentsNotificationSchema,
  exportCompletionNotificationSchema,
  staleIdeasNotificationSchema,
  aiDashboardGenerationCompletedNotificationSchema,
]);

export type NotificationDTO = z.infer<typeof notificationDTOSchema>;

export const notificationSortableFields = ['id'] as const;

export const AREA_FILTERS = [
  'all',
  'jira',
  'members',
  'integration',
  'capacity',
  'comments',
  'feedback',
  'planning',
  'portfolio',
  'export',
  'insights',
  'recommendations',
] as const;
export type AreaFilter = (typeof AREA_FILTERS)[number];

const excludeAreaSchema = z.enum(AREA_FILTERS).exclude(['all']).optional();

export const unreadCountQueryDTOSchema = z.object({ excludeArea: excludeAreaSchema });

export const inboxQueryDTOSchema = createPageQueryDTOSchema({
  sortableFields: notificationSortableFields,
  defaultSortBy: 'id',
  defaultSortOrder: 'desc',
}).extend({
  unread: z
    .string()
    .transform((v) => v.toLowerCase())
    .pipe(z.enum(['true', 'false']))
    .transform((v) => v === 'true')
    .optional(),
  area: z.enum(AREA_FILTERS).optional(),
  excludeArea: excludeAreaSchema,
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});

export const organizationPreferenceItemSchema = z.object({
  category: organizationCategorySchema,
  channel: notificationChannelSchema,
  enabled: z.boolean(),
});

export const subscriptionPreferenceItemSchema = z.object({
  category: subscriptionCategorySchema,
  channel: notificationChannelSchema,
  enabled: z.boolean(),
});

export const preferencesResponseDTOSchema = z.object({
  organization: z.array(organizationPreferenceItemSchema),
  subscription: z.array(subscriptionPreferenceItemSchema),
  globalDisabled: z.boolean(),
});

export type PreferencesResponseDTO = z.infer<typeof preferencesResponseDTOSchema>;

export const globalPreferenceUpdateDTOSchema = z.object({
  disabled: z.boolean(),
});

export type GlobalPreferenceUpdateDTO = z.infer<typeof globalPreferenceUpdateDTOSchema>;

export const preferenceUpdateDTOSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('organization'),
    category: organizationCategorySchema,
    channel: notificationChannelSchema,
    enabled: z.boolean(),
  }),
  z.object({
    type: z.literal('subscription'),
    category: subscriptionCategorySchema,
    channel: notificationChannelSchema,
    enabled: z.boolean(),
  }),
]);

export type PreferenceUpdateDTO = z.infer<typeof preferenceUpdateDTOSchema>;

export const notificationEntityRefSchema = z.object({
  entityType: z.string().min(1),
  entityId: z.string().min(1),
});

export type NotificationEntityRef = z.infer<typeof notificationEntityRefSchema>;

export const notificationEnvelopeMetadataSchema = z.object({
  entityType: z.string().min(1).optional(),
  entityId: z.string().min(1).optional(),
  // Nearest parent first, supplied by the producer that owns the hierarchy.
  ancestors: z.array(notificationEntityRefSchema).optional(),
  assigneeIds: z.array(z.string()).optional(),
  mentionedIds: z.array(z.string()).optional(),
  participantIds: z.array(z.string()).optional(),
});

export type NotificationEnvelopeMetadata = z.infer<typeof notificationEnvelopeMetadataSchema>;

const dispatchBaseSchema = notificationEnvelopeMetadataSchema.extend({
  targetOrganizationId: z.string(),
  targetUserIds: z.array(z.string()),
  expiresAt: z.string().optional(),
  suppressEmail: z.boolean().optional(),
});

const dispatchInvitesSchema = dispatchBaseSchema.extend({
  variables: invitesVariablesSchema,
});

const dispatchWorkIntegrationReauthSchema = dispatchBaseSchema.extend({
  variables: workIntegrationReauthVariablesSchema,
});

const dispatchWorkIntegrationScheduleDisabledSchema = dispatchBaseSchema.extend({
  variables: workIntegrationScheduleDisabledVariablesSchema,
});

const dispatchWorkIntegrationSyncErrorSchema = dispatchBaseSchema.extend({
  variables: workIntegrationSyncErrorVariablesSchema,
});

const dispatchWorkIntegrationSetupCompleteSchema = dispatchBaseSchema.extend({
  variables: workIntegrationSetupCompleteVariablesSchema,
});

const dispatchWorkIntegrationDisconnectedSchema = dispatchBaseSchema.extend({
  variables: workIntegrationDisconnectedVariablesSchema,
});

const dispatchWorkIntegrationExcludedTeamsSchema = dispatchBaseSchema.extend({
  variables: workIntegrationExcludedTeamsVariablesSchema,
});

const dispatchWorkIntegrationPublishCompleteSchema = dispatchBaseSchema.extend({
  variables: workIntegrationPublishCompleteVariablesSchema,
});

const dispatchCapacityOrphanedInstanceSchema = dispatchBaseSchema.extend({
  variables: capacityOrphanedInstanceVariablesSchema,
});

const dispatchPortfolioCreatedSchema = dispatchBaseSchema.extend({
  variables: portfolioCreatedVariablesSchema,
});

const dispatchTeamAssignmentChangedSchema = dispatchBaseSchema.extend({
  variables: teamAssignmentChangedVariablesSchema,
});

const dispatchPlanPublishedSchema = dispatchBaseSchema.extend({
  variables: planPublishedVariablesSchema,
});

const dispatchScenarioOperationFailedSchema = dispatchBaseSchema.extend({
  variables: scenarioOperationFailedVariablesSchema,
});

const dispatchFeedbackSchema = dispatchBaseSchema.extend({
  variables: feedbackVariablesSchema,
});

const dispatchIdeaCommentsSchema = dispatchBaseSchema.extend({
  variables: ideaCommentsVariablesSchema,
});

const dispatchCommentMentionSchema = dispatchBaseSchema.extend({
  variables: commentMentionVariablesSchema,
});

const dispatchExportCompletionSchema = dispatchBaseSchema.extend({
  variables: exportCompletionVariablesSchema,
});

const dispatchAiDashboardGenerationCompletedSchema = dispatchBaseSchema.extend({
  variables: aiDashboardGenerationCompletedVariablesSchema,
});

const dispatchStaleIdeasSchema = dispatchBaseSchema.extend({
  variables: staleIdeasVariablesSchema,
});

const dispatchDemandFrameworkChangedSchema = dispatchBaseSchema.extend({
  variables: demandFrameworkChangedVariablesSchema,
});

const dispatchDemandReviewerAssignedSchema = dispatchBaseSchema.extend({
  variables: demandReviewerAssignedVariablesSchema,
});

const dispatchDemandStageChangedSchema = dispatchBaseSchema.extend({
  variables: demandStageChangedVariablesSchema,
});

const dispatchObjectiveHealthChangedSchema = dispatchBaseSchema.extend({
  variables: objectiveHealthChangedVariablesSchema,
});

const dispatchObjectiveDriftDetectedSchema = dispatchBaseSchema.extend({
  variables: objectiveDriftDetectedVariablesSchema,
});

const dispatchNewCriticalInsightSchema = dispatchBaseSchema.extend({
  variables: newCriticalInsightVariablesSchema,
});

const dispatchNewHighInsightSchema = dispatchBaseSchema.extend({
  variables: newHighInsightVariablesSchema,
});

const dispatchCreditPoolLowSchema = dispatchBaseSchema.extend({
  variables: creditPoolLowVariablesSchema,
});

const dispatchRecommendationAssignedSchema = dispatchBaseSchema.extend({
  variables: recommendationAssignedVariablesSchema,
});

const dispatchRecommendationCompletedSchema = dispatchBaseSchema.extend({
  variables: recommendationCompletedVariablesSchema,
});

const dispatchRecommendationDismissedSchema = dispatchBaseSchema.extend({
  variables: recommendationDismissedVariablesSchema,
});

const dispatchRecommendationReopenedSchema = dispatchBaseSchema.extend({
  variables: recommendationReopenedVariablesSchema,
});

const dispatchRecommendationRestoredSchema = dispatchBaseSchema.extend({
  variables: recommendationRestoredVariablesSchema,
});

const dispatchRecommendationUnassignedSchema = dispatchBaseSchema.extend({
  variables: recommendationUnassignedVariablesSchema,
});

const dispatchRecommendationReviewSchema = dispatchBaseSchema.extend({
  variables: recommendationReviewVariablesSchema,
});

const dispatchRecommendationAutoclosedSchema = dispatchBaseSchema.extend({
  variables: recommendationAutoclosedVariablesSchema,
});

const dispatchRecommendationStaleSchema = dispatchBaseSchema.extend({
  variables: recommendationStaleVariablesSchema,
});

const dispatchRecommendationKeptSchema = dispatchBaseSchema.extend({
  variables: recommendationKeptVariablesSchema,
});

export const dispatchNotificationDTOSchema = z.union([
  dispatchInvitesSchema,
  dispatchWorkIntegrationReauthSchema,
  dispatchWorkIntegrationScheduleDisabledSchema,
  dispatchWorkIntegrationSyncErrorSchema,
  dispatchWorkIntegrationSetupCompleteSchema,
  dispatchWorkIntegrationDisconnectedSchema,
  dispatchWorkIntegrationExcludedTeamsSchema,
  dispatchWorkIntegrationPublishCompleteSchema,
  dispatchCapacityOrphanedInstanceSchema,
  dispatchPortfolioCreatedSchema,
  dispatchTeamAssignmentChangedSchema,
  dispatchPlanPublishedSchema,
  dispatchScenarioOperationFailedSchema,
  dispatchFeedbackSchema,
  dispatchIdeaCommentsSchema,
  dispatchCommentMentionSchema,
  dispatchExportCompletionSchema,
  dispatchStaleIdeasSchema,
  dispatchAiDashboardGenerationCompletedSchema,
  dispatchDemandFrameworkChangedSchema,
  dispatchDemandReviewerAssignedSchema,
  dispatchDemandStageChangedSchema,
  dispatchObjectiveHealthChangedSchema,
  dispatchObjectiveDriftDetectedSchema,
  dispatchNewCriticalInsightSchema,
  dispatchNewHighInsightSchema,
  dispatchCreditPoolLowSchema,
  dispatchRecommendationAssignedSchema,
  dispatchRecommendationCompletedSchema,
  dispatchRecommendationDismissedSchema,
  dispatchRecommendationReopenedSchema,
  dispatchRecommendationRestoredSchema,
  dispatchRecommendationUnassignedSchema,
  dispatchRecommendationReviewSchema,
  dispatchRecommendationAutoclosedSchema,
  dispatchRecommendationStaleSchema,
  dispatchRecommendationKeptSchema,
]);

export type DispatchNotificationDTO = z.infer<typeof dispatchNotificationDTOSchema>;

export const NOTIFICATION_CHANNEL_PREFERENCE_REASON_SCOPE_KEYS = [
  'assigned',
  'mentioned',
  'participating',
  'watching',
] as const;

// Account-scoped categories only (org-wide facts with no watchable subject). Entity-scoped
// categories get their channel preference from the watch row's nullable `channels` column instead,
// so allowing them here would reintroduce the one-row-per-category model NEX-17562 replaces.
// trials/trial_extended are account-scoped in the registry but deliberately absent: nothing
// dispatches them, so they must not accept channel preferences.
export const NOTIFICATION_CHANNEL_PREFERENCE_ACCOUNT_CATEGORIES = [
  'invites',
  'work_integration_reauth',
  'capacity_orphaned_instance',
  'credit_pool_low',
  'portfolio_created',
  'export_completion',
  'ai_dashboard_generation_completed',
] as const satisfies readonly NotificationCategory[];

export const NOTIFICATION_CHANNEL_PREFERENCE_SCOPE_KEYS = [
  ...NOTIFICATION_CHANNEL_PREFERENCE_REASON_SCOPE_KEYS,
  ...NOTIFICATION_CHANNEL_PREFERENCE_ACCOUNT_CATEGORIES,
] as const;

export type NotificationChannelPreferenceReasonScopeKey =
  (typeof NOTIFICATION_CHANNEL_PREFERENCE_REASON_SCOPE_KEYS)[number];
export type NotificationChannelPreferenceScopeKey =
  (typeof NOTIFICATION_CHANNEL_PREFERENCE_SCOPE_KEYS)[number];

export const channelPreferenceScopeKeySchema = z.enum(NOTIFICATION_CHANNEL_PREFERENCE_SCOPE_KEYS);

export const channelPreferenceUpdateDTOSchema = z.object({
  scopeKey: channelPreferenceScopeKeySchema,
  channel: notificationChannelSchema,
  enabled: z.boolean(),
});

export const channelPreferenceDTOSchema = channelPreferenceUpdateDTOSchema.extend({
  isDefault: z.boolean(),
  locked: z.boolean(),
});

export const channelPreferencesResponseDTOSchema = z.object({
  items: z.array(channelPreferenceDTOSchema),
});

export type ChannelPreferenceDTO = z.infer<typeof channelPreferenceDTOSchema>;
export type ChannelPreferencesResponseDTO = z.infer<typeof channelPreferencesResponseDTOSchema>;

export const NOTIFICATION_DELIVERY_REASONS = [
  'mandatory',
  'mentioned',
  'assigned',
  'participating',
  'watching',
  'account',
] as const;
export type NotificationDeliveryReason = (typeof NOTIFICATION_DELIVERY_REASONS)[number];
export const notificationDeliveryReasonSchema = z.enum(NOTIFICATION_DELIVERY_REASONS);
